/* eslint-disable @typescript-eslint/no-unused-vars */
import { Avatar, Button, CloseButton, Flex, Grid, Group, Input, NumberInput, Select, SelectProps, Text } from "@mantine/core"
import { useForm } from "@mantine/form"
import { ImageIcon } from "@phosphor-icons/react";
import React, { useState } from "react"
import useSwr from "swr"
import * as yup from "yup"
import { yupResolver } from "mantine-form-yup-resolver"
import { useTranslation } from "react-i18next"
import { i18n } from "@dehub/theme"
import { GoodsApi } from "../../apis"
import { currencyFormat } from "../../utils"
import { message } from "../../utils/message"
import { ModalFooter, PageLayout } from "../layout"

const schema = yup.object({
  variantId: yup.string().required(() => i18n.t("common:validation.required")).nullable(),
  quantity: yup.number().min(1, () => i18n.t("common:validation.required")).typeError(() => i18n.t("common:validation.required")).required(() => i18n.t("common:validation.required")).nullable(),
})

export function BuyerSalesDynamicAdditionalForm({ action, setFieldValue, setAction, values }: any) {
  const [query, setQuery] = useState<string | any>("")
  const [loading, setLoading] = useState<boolean>(false)
  const { t } = useTranslation(["order", "common"])

  const { data: variantList = [] } = useSwr(
    `/erp/invoice/variant/list?query=${query}`,
    async () => {
      const response = await GoodsApi.variant({
        filter: {
          query: "",
          categoryId: "",
          brandId: "",
          orderType: "PURCHASE",
        },
        offset: {
          page: 1,
          limit: 100,
        },
        businessId: values?.types ? values?.receiverBusinessId : values?.supplierId,
      })
      return {
        ...response,
        // Keep out-of-stock rows that are flagged "нөөцгүй зарах" (allowSaleWithoutStock
        // or backorderable) — they remain orderable via backorder.
        rows: response.rows?.filter((item: any) => item.inventoryTotalAvialable > 0 || item.allowSaleWithoutStock || item.backorderable) || [],
      }
    },
    {
      fallback: [],
    },
  )

  const [data] = useState<any>({
    id: undefined,
    barCode: undefined,
    brand: undefined,
    category: undefined,
    erpCode: undefined,
    hasTax: undefined,
    hasVat: undefined,
    image: undefined,
    nameApp: undefined,
    nameBill: undefined,
    nameEng: undefined,
    nameWeb: undefined,
    price: undefined,
    priceStartDate: undefined,
    refCode: undefined,
    skuCode: undefined,
    supplier: undefined,
    taxAmount: undefined,
    unit: undefined,
    vatAmount: undefined,
    inventories: undefined,

    ...(action[0] === "edit" && action[1]),
  })

  const form = useForm<any>({
    mode: "controlled",
    initialValues: data,
    validate: yupResolver(schema),
  })

  const onSearchService = async (query: any) => {
    let timeout
    if (query) {
      if (timeout) clearTimeout(timeout)
      timeout = setTimeout(async () => {
        setQuery(query)
      }, 1000)
    } else {
      setQuery("")
    }
  }

  const units = variantList?.rows?.flatMap((vl: any) =>
    vl?.prices?.map((price: any) => ({
      label: price.unitName,
      value: price.unitId,
    })),
  )

  const getVariant = async (id: string) => {
    let res = null

    try {
      res = variantList.rows.filter((item: any) => item?.id === id)[0]
      form.setFieldValue("id", res.id)
      form.setFieldValue("refCode", res.refCode)
      form.setFieldValue("skuCode", res.skuCode)
      form.setFieldValue("barCode", res.barCode)
      form.setFieldValue("erpCode", res.erpCode)
      form.setFieldValue("nameMon", res.nameMon)
      form.setFieldValue("nameEng", res.nameEng)
      form.setFieldValue("nameBill", res.nameBill)
      form.setFieldValue("nameApp", res.nameApp)
      form.setFieldValue("nameWeb", res.nameWeb)
      form.setFieldValue("image", res.image)
      form.setFieldValue("category", res.category)
      form.setFieldValue("brand", res.brand)
      form.setFieldValue("unit", res.unit)
      form.setFieldValue("unitConvertType", res.unitConvertType)
      form.setFieldValue("unitConvertValue", res.unitConvertValue)
      form.setFieldValue("vatType", res.vatType)
      form.setFieldValue("vatPercent", res.vatPercent)
      form.setFieldValue("vatAmount", res.vatAmount)
      form.setFieldValue("hasTax", res.hasTax)
      form.setFieldValue("taxPercent", res.taxPercent)
      form.setFieldValue("taxAmount", res.taxAmount)
      form.setFieldValue("price", res.price)
      form.setFieldValue("discountType", res.discountType)
      form.setFieldValue("discountValue", res.discountValue)
      form.setFieldValue("onDeliveryQuantity", res.onDeliveryQuantity)
      form.setFieldValue("warehouseQuantity", res.warehouseQuantity)
      form.setFieldValue("quantity", res.quantity)
      form.setFieldValue("inventories", res.inventories)
      form.setFieldValue("prices", res.prices)
    } catch (error) {}
  }

  const onSubmit = async (formValues: any) => {
    setLoading(true)
    if (action[1]?.lines?.some((item: any) => item.id === formValues.variantId)) {
      message.error(t("order:buyerSales.productAlreadyAdded"))
    } else if (values?.lines?.some((item: any) => item.variantId === formValues?.variantId)) {
      message.error(t("order:buyerSales.productAlreadyAdded"))
    } else {
      setFieldValue("lines", [...(action[1]?.lines || []), formValues])
      setAction([])
    }
    setLoading(false)
  }

  const renderSelectOption: SelectProps["renderOption"] = ({ option }: { option: any }) => {
    return (
      <Group>
        <Avatar src={option?.item?.image}>
          <ImageIcon />
        </Avatar>
        <div>
          <Text size="sm">{option.label}</Text>
          <Text size="sm" opacity={0.65}>
            {option?.item?.unit || "-"} / {currencyFormat(option?.item?.price || 0)}
          </Text>
        </div>
      </Group>
    )
  }

  return (
    <PageLayout title={t("order:buyerSales.addProduct")} subTitle={t("order:buyerSales.addProductSub")} extra={[<CloseButton onClick={() => setAction([])} key={1}></CloseButton>]}>
      <Grid>
        <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
          <Select
            {...form.getInputProps("variantId")}
            renderOption={renderSelectOption}
            searchable
            clearable
            placeholder={t("order:buyerSales.productPlaceholder")}
            label={t("order:buyerSales.productLabel")}
            onSearchChange={(e) => onSearchService(e)}
            onChange={(e: any) => {
              form.setFieldValue("variantId", e)
              form.setFieldValue("quantity", undefined)
              getVariant(e)
            }}
            data={(variantList.rows ?? []).map((item: any) => {
              return {
                label: `${item.nameMon} ${item.optionValueNames ? " - " + item.optionValueNames : ""}`,
                value: item.id,
                item: item,
              }
            })}
            required
            withAsterisk
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
          <Select
            {...form.getInputProps("unitId")}
            searchable
            clearable
            placeholder={t("order:buyerSales.unitPlaceholder")}
            label={t("order:buyerSales.unitLabel")}
            onSearchChange={(e) => onSearchService(e)}
            onChange={(value: string | null) => {
              form.setFieldValue("unitId", value)
              getVariant(value!)
            }}
            data={units ?? []}
            required
            withAsterisk
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
          <NumberInput
            {...form.getInputProps("quantity")}
            min={0}
            placeholder={t("order:buyerSales.quantityPlaceholder")}
            label={t("order:buyerSales.quantityLabel")}
            required
            withAsterisk
            hideControls
            disabled={!form.values?.variantId}
            onChange={(val: number | string) => {
              const numVal = typeof val === "number" ? val : Number(val)
              if (!numVal || numVal < 0) return
              form.setFieldValue("quantity", numVal)
            }}
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
          <Input.Wrapper label={`${t("order:buyerSales.unitPrice")}  /${form.values?.unit || t("order:buyerSales.piece")}/`}>
            <Flex align={"center"} h="36px">
              <Text size="sm" fw={500} c="var(--mantine-color-dark-5)">
                {currencyFormat(form.values?.price || 0)}
              </Text>
            </Flex>
          </Input.Wrapper>
        </Grid.Col>
        <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
          <Input.Wrapper label={t("order:buyerSales.totalPrice")}>
            <Flex align={"center"} h="36px">
              <Text size="sm" fw={500} c="var(--mantine-color-dark-5)">
                {currencyFormat((form.values?.price || 0) * (form.values.quantity || 0))}
              </Text>
            </Flex>
          </Input.Wrapper>
        </Grid.Col>
      </Grid>
      <ModalFooter>
        <Button variant="default" onClick={() => setAction([])}>
          {t("common:actions.back")}
        </Button>
        <Button
          loading={loading}
          disabled={!form.values?.variantId}
          onClick={async () => {
            const { hasErrors } = form.validate()
            if (!hasErrors) onSubmit(form.values)
          }}>
          {t("common:actions.save")}
        </Button>
      </ModalFooter>
    </PageLayout>
  )
}
